import {
  Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { BaseListboxEditor } from './base-editor';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';
import { ChildTableA11yService } from '../../services/child-table-a11y.service';
import { ChildTablePositioningService } from '../../services/child-table-positioning.service';

@Component({
  selector: 'ct-multiselect-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cell-listbox-wrapper">
      <button #trigger class="cell-listbox-trigger" type="button"
        [attr.data-value]="getCommaSeparated()"
        role="combobox"
        [attr.aria-expanded]="true"
        aria-haspopup="listbox"
        [attr.aria-controls]="listboxId"
        [attr.aria-activedescendant]="activeDescendantId"
        [attr.aria-label]="column.label + ': ' + selectedCount + ' selected'"
        [attr.aria-describedby]="ariaDescribedBy"
        [attr.aria-invalid]="hasError || null"
        [attr.aria-required]="isRequired || null"
        (keydown)="onListKeydown($event)">
        {{ selectedLabel || 'Select...' }}
        <lucide-angular class="select-chevron" name="chevron-down" [size]="12" aria-hidden="true"></lucide-angular>
      </button>
      <div class="cell-listbox" [id]="listboxId" role="listbox" aria-multiselectable="true"
        [attr.aria-label]="column.label + ' options'"
        style="display: none;"
        (mousedown)="$event.stopPropagation()">
        <div *ngFor="let option of column.selectOptions; let i = index"
          class="listbox-option" role="option"
          [id]="optionId(i)"
          [attr.aria-selected]="isSelected(option.value)"
          [class.selected]="isSelected(option.value)"
          [class.highlighted]="highlightIndex === i"
          (mousedown)="$event.stopPropagation()"
          (click)="toggleOption(option.value, $event)"
          (mouseenter)="highlightIndex = i">
          <span class="multiselect-check" [class.checked]="isSelected(option.value)">
            <lucide-angular *ngIf="isSelected(option.value)" name="check" [size]="12" aria-hidden="true"></lucide-angular>
          </span>
          {{ option.label }}
        </div>
      </div>
    </div>
  `,
  styles: [CELL_EDITOR_STYLES, `
    .multiselect-check {
      display: inline-flex;
      width: 16px;
      height: 16px;
      border: 1px solid var(--og-border-default, rgba(0,0,0,0.31));
      border-radius: var(--og-radius-sm, 4px);
      margin-right: 6px;
      align-items: center;
      justify-content: center;
    }
    .multiselect-check.checked {
      background: var(--og-primary, #4b3fff);
      border-color: var(--og-primary, #4b3fff);
      color: white;
    }
  `],
})
export class MultiselectEditorComponent extends BaseListboxEditor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('trigger') trigger!: ElementRef<HTMLButtonElement>;
  selectedValues: Set<string | number> = new Set();

  get listboxId(): string { return `ms-listbox-${this.column.key}-${this.rowId}`; }
  get activeDescendantId(): string { return this.optionId(this.highlightIndex); }
  optionId(i: number): string { return `ms-option-${this.column.key}-${this.rowId}-${i}`; }

  get selectedCount(): number { return this.selectedValues.size; }
  get selectedLabel(): string {
    if (this.selectedValues.size === 0) return '';
    const labels = this.column.selectOptions
      ?.filter(o => this.selectedValues.has(o.value))
      .map(o => o.label) ?? [];
    return labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`;
  }

  constructor(
    protected override readonly a11y: ChildTableA11yService,
    private cdr: ChangeDetectorRef,
    private positioning: ChildTablePositioningService,
  ) { super(); }

  ngOnInit(): void {
    const raw = this.value;
    if (Array.isArray(raw)) {
      this.selectedValues = new Set(raw.map(String));
    } else if (typeof raw === 'string' && raw) {
      this.selectedValues = new Set(raw.split(',').map(v => v.trim()));
    }
    this.editingValue = this.getCommaSeparated();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const triggerEl = this.trigger?.nativeElement;
      if (!triggerEl) return;
      triggerEl.focus();
      const listbox = triggerEl.closest('.cell-listbox-wrapper')?.querySelector('.cell-listbox') as HTMLElement;
      if (listbox) {
        listbox.style.minWidth = Math.max(triggerEl.getBoundingClientRect().width, 180) + 'px';
        this.positioning.openMenu('cell-multiselect', triggerEl, listbox, () => this.confirmAndClose());
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void { this.positioning.closeMenu('cell-multiselect'); }

  isSelected(value: string | number): boolean { return this.selectedValues.has(String(value)); }

  toggleOption(value: string | number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const key = String(value);
    if (this.selectedValues.has(key)) {
      this.selectedValues.delete(key);
    } else {
      this.selectedValues.add(key);
    }
    this.editingValue = this.getCommaSeparated();
    this.a11y.announce(`${this.selectedValues.has(value) ? 'Selected' : 'Deselected'}, ${this.selectedCount} total`);
    this.cdr.markForCheck();
  }

  confirmAndClose(): void {
    this.save.emit(this.getCommaSeparated());
  }

  onListKeydown(event: KeyboardEvent): void {
    const options = this.column.selectOptions;
    if (!options?.length) return;
    
    if (this.handleListboxNavigation(event, options)) {
      return;
    }

    switch (event.key) {
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.toggleOption(options[this.highlightIndex].value);
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        this.confirmAndClose();
        break;
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        this.tabSave.emit({ value: this.getCommaSeparated(), shiftKey: event.shiftKey });
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.cancel.emit();
        break;
    }
    this.cdr.markForCheck();
  }

  getCommaSeparated(): string {
    return Array.from(this.selectedValues).join(',');
  }
}
